import React, {
    forwardRef,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import './FileItem.scss';
import {
    ChatbubbleOutline,
    DocumentOutline,
    DownloadOutline,
    EllipsisVertical,
    FolderOutline,
    InformationOutline,
    MoveOutline,
    PencilOutline,
    SearchOutline,
    TrashOutline,
} from 'react-ionicons';
import { Dropdown, Form } from 'react-bootstrap';
import FileExtension from './FileExtension';
import moment from 'moment';
import FileExplorerContext from '../../contexts/FileExplorerContext';
import DeleteElementModal from './Modals/DeleteElementModal';
import EditElementModal from './Modals/EditElementModal';
import FilePreviewModal from './Modals/FilePreviewModal';
import MoveElementModal from './Modals/MoveElementModal';
import FileInformationsModal from './Modals/FileInformationsModal';
import FileCommentsModal from './Modals/FileCommentsModal';
import { toast } from 'react-toastify';
import { getBulkDownload, getFileDownload } from '../../api/actions/Documents';
import { connect } from 'react-redux';

const CustomToggle = forwardRef(({ onClick }, ref) => (
    <div onClick={onClick} ref={ref}>
        <EllipsisVertical color="gray" />
    </div>
));

const FileItem = ({ element, profile, session, onCheckItem, isSelected }) => {
    const { onOpenFolder } = useContext(FileExplorerContext);

    const [showPreview, setShowPreview] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showMove, setShowMove] = useState(false);
    const [showInformations, setShowInformations] = useState(false);
    const [showComments, setShowComments] = useState(false);

    const formattedDate = useMemo(() => {
        if (element.isDirectory && !element.lastActivity) {
            return '-';
        }

        if (element.isDirectory && element.lastActivity) {
            const parsedDate = moment(element.lastActivity);

            return parsedDate.format('MM/DD/YYYY HH:MM A');
        }

        const parsedDate = moment(element.file.updatedAt);

        return parsedDate.format('MM/DD/YYYY HH:MM A');
    }, [element]);

    const formattedSize = useMemo(() => {
        if (element.isDirectory) {
            return '-';
        }

        const parsedSize = parseInt(element.file.fileSize) / 1000;

        if (parsedSize > 1000) {
            const mbSize = parsedSize / 1000;

            if (mbSize > 1000) {
                const gbSize = mbSize / 1000;

                return `${gbSize.toFixed(2)} GB`;
            }
            return `${mbSize.toFixed(2)} MB`;
        }
        return `${parsedSize.toFixed(2)} KB`;
    }, [element]);

    const togglePreview = useCallback(() => {
        setShowPreview((prev) => !prev);
    }, []);

    const onClick = useCallback(() => {
        if (!element.isDirectory) {
            togglePreview();
        } else {
            onOpenFolder(element.id);
        }
    }, [element, onOpenFolder, togglePreview]);

    const toggleDelete = useCallback((event) => {
        setShowDelete((prev) => !prev);
    }, []);

    const toggleEdit = useCallback((event) => {
        setShowEdit((prev) => !prev);
    }, []);

    const toggleMove = useCallback((event) => {
        setShowMove((prev) => !prev);
    }, []);

    const toggleInformations = useCallback((event) => {
        setShowInformations((prev) => !prev);
    }, []);

    const toggleComments = useCallback((event) => {
        setShowComments((prev) => !prev);
    }, []);

    const onDownload = useCallback(
        async (e) => {
            if (e && e.preventDefault) {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
            }

            try {
                if (element.isDirectory) {
                    const requestBody = {
                        folderIds: [element.id],
                        filename: element.name,
                    };

                    const response = await fetch(getBulkDownload(), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) {
                        throw new Error('Folder download failed');
                    }

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.download = element.name + '.zip';
                    link.href = url;
                    link.click();
                    window.URL.revokeObjectURL(url);
                } else {
                    const url =
                        getFileDownload(profile.entityId, element.file.id) +
                        '&download=true';

                    window.open(url, '_blank');
                }
            } catch (error) {
                console.error('Error downloading file:', error);
                toast.error(
                    'Something wrong happened while downloading the file. Please try again.',
                    {
                        className: 'custom-toast',
                    }
                );
            }
        },
        [element, profile]
    );

    const onCheckChange = useCallback(() => {
        onCheckItem(element);
    }, [onCheckItem, element]);

    return (
        <tr>
            <td style={{ width: 20 }} className="rounded">
                <Form.Check checked={isSelected} onChange={onCheckChange} />
            </td>
            <td className="rounded" onClick={onClick}>
                <div className="d-flex align-items-center">
                    <div className="folder-icon">
                        {element.isDirectory ? (
                            <FolderOutline color="inherit" />
                        ) : (
                            <DocumentOutline color="inherit" />
                        )}
                    </div>
                    <p className="ps-2">
                        {element.isDirectory
                            ? element.name
                            : element.file.fileName}
                    </p>
                </div>
            </td>
            <td onClick={onClick}>
                <FileExtension
                    extension={
                        element.isDirectory ? null : element.file.fileExtension
                    }
                    isDirectory={element.isDirectory}
                />
            </td>
            <td onClick={onClick}>{formattedSize}</td>
            <td onClick={onClick}>{formattedDate}</td>
            <td className="rounded" style={{ width: 40 }}>
                <div className="d-flex justify-content-end pe-4 dropdown-container">
                    <Dropdown drop="start" align={{ lg: 'start' }}>
                        <Dropdown.Toggle as={CustomToggle}>
                            Options
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {!element.isDirectory && (
                                <>
                                    <Dropdown.Item
                                        as="button"
                                        onClick={togglePreview}
                                        className="d-flex align-items-center"
                                    >
                                        <SearchOutline
                                            className="me-2"
                                            style={{ marginBottom: 2 }}
                                        />
                                        Preview
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        className="d-flex align-items-center"
                                        as="button"
                                        onClick={toggleComments}
                                    >
                                        <ChatbubbleOutline
                                            className="me-2"
                                            style={{ marginBottom: 2 }}
                                        />
                                        Comments
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        className="d-flex align-items-center"
                                        as="button"
                                        onClick={toggleInformations}
                                    >
                                        <InformationOutline
                                            className="me-2"
                                            style={{ marginBottom: 2 }}
                                        />
                                        Informations
                                    </Dropdown.Item>
                                </>
                            )}
                            <Dropdown.Item
                                onClick={toggleMove}
                                as="button"
                                className="d-flex align-items-center"
                            >
                                <MoveOutline
                                    className="me-2"
                                    style={{ marginBottom: 2 }}
                                />
                                Move
                            </Dropdown.Item>
                            <Dropdown.Item
                                className="d-flex align-items-center"
                                as="button"
                                onClick={onDownload}
                            >
                                <DownloadOutline
                                    className="me-2"
                                    style={{ marginBottom: 2 }}
                                />
                                Download
                            </Dropdown.Item>
                            <Dropdown.Item
                                as="button"
                                className="d-flex align-items-center"
                                onClick={toggleEdit}
                            >
                                <PencilOutline
                                    className="me-2"
                                    style={{ marginBottom: 2 }}
                                />
                                Rename
                            </Dropdown.Item>
                            <Dropdown.Item
                                as="button"
                                className="d-flex align-items-center"
                                onClick={toggleDelete}
                            >
                                <TrashOutline
                                    className="me-2"
                                    style={{ marginBottom: 2 }}
                                />
                                Delete
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </td>
            {!element.isDirectory && (
                <>
                    <FilePreviewModal
                        file={element.file}
                        show={showPreview}
                        onClose={togglePreview}
                    />
                    <FileInformationsModal
                        onClose={toggleInformations}
                        show={showInformations}
                        element={element}
                    />
                    <FileCommentsModal
                        show={showComments}
                        onClose={toggleComments}
                        element={element}
                    />
                </>
            )}

            <DeleteElementModal
                onClose={toggleDelete}
                show={showDelete}
                element={element}
            />
            <EditElementModal
                onClose={toggleEdit}
                show={showEdit}
                element={element}
            />
            <MoveElementModal
                onClose={toggleMove}
                show={showMove}
                element={element}
            />
        </tr>
    );
};

const mapStateToProps = (appState) => ({
    profile: appState.userProfile,
    session: appState.session,
});

export default connect(mapStateToProps)(FileItem);
